import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Bubble } from '../components/Bubble';
import { LEADERBOARD_ROW_EXTENT, LeaderboardRow } from '../components/LeaderboardRow';
import type { LeaderboardEntry } from '../models/types';
import { meIndex, useLeaderboard } from '../state/leaderboard';
import { usePlayer } from '../state/player';
import { useShell } from '../state/shell';

/** Distance from either end of the list at which the next page is requested. */
const LOAD_THRESHOLD = 200;

/** The Ranks tab: a list anchored on the signed-in player that grows in both directions as they scroll. */
export function LeaderboardScreen() {
  const { data, error, load, refresh } = useLeaderboard();
  const list = useRef<HTMLDivElement>(null);
  const prev = useRef<{ generation: number; firstId: string | null; height: number } | null>(null);

  useEffect(() => {
    if (!useLeaderboard.getState().data) void load();
  }, [load]);

  const maybeLoad = useCallback(() => {
    const el = list.current;
    if (!el) return;
    if (el.scrollTop < LOAD_THRESHOLD) void useLeaderboard.getState().loadUp();
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_THRESHOLD) void useLeaderboard.getState().loadDown();
  }, []);

  // A reloaded window re-centres on the player; a prepended page keeps the rows on screen where they were.
  useLayoutEffect(() => {
    const el = list.current;
    if (!el || !data) return;
    const first = data.entries[0]?.playerId ?? null;
    const before = prev.current;
    if (!before || before.generation !== data.generation) {
      // Put the player's row mid-screen, but never leave blank space above the first entry.
      const aboveExtent = meIndex(data) * LEADERBOARD_ROW_EXTENT;
      el.scrollTop = Math.max(0, aboveExtent - (el.clientHeight / 2 - LEADERBOARD_ROW_EXTENT / 2));
    } else if (before.firstId !== first) {
      el.scrollTop += el.scrollHeight - before.height;
    }
    prev.current = { generation: data.generation, firstId: first, height: el.scrollHeight };
    maybeLoad();
  }, [data, maybeLoad]);

  const open = (entry: LeaderboardEntry) => {
    const myId = usePlayer.getState().player?.profile.playerId;
    if (entry.isMe || entry.playerId === myId) {
      useShell.getState().select('you');
      return;
    }
    useShell.getState().openProfile({ playerId: entry.playerId, rank: entry.rank, displayName: entry.displayName });
  };

  return (
    <section className="screen" aria-label="Ranks">
      <h1 className="app-bar">Ranks</h1>
      {data ? (
        <>
          <div style={{ padding: '8px 16px 12px', flex: 'none' }}>
            <Bubble style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 32 }} aria-hidden="true">🏆</span>
              <div style={{ flex: 1 }}>
                <div className="t-title-l" style={{ fontWeight: 900 }}>#{data.me.rank} of {data.totalPlayers} hunters</div>
                <div>{data.me.score} things found</div>
              </div>
              <button type="button" className="icon-btn" aria-label="Refresh leaderboard" onClick={() => void refresh()}>⟳</button>
            </Bubble>
          </div>
          <div className="lb-list" ref={list} onScroll={maybeLoad} style={{ overflowAnchor: 'none' }} data-testid="lb-list">
            {data.loadingUp && <EdgeSpinner top />}
            {data.entries.map((e) => (
              <LeaderboardRow key={e.playerId} entry={e} onClick={() => open(e)} />
            ))}
            {data.loadingDown && <EdgeSpinner />}
          </div>
        </>
      ) : error ? (
        <div className="center-fill">Could not load the leaderboard.<br />{error}</div>
      ) : (
        <div className="center-fill"><span className="spinner dark" role="progressbar" aria-label="Loading" /></div>
      )}
    </section>
  );
}

function EdgeSpinner({ top = false }: { top?: boolean }) {
  // Overlaid (not in flow) so showing it never shifts the rows under the reader's thumb.
  return (
    <div style={top ? { position: 'sticky', top: 4, height: 0, zIndex: 1 } : { position: 'sticky', bottom: 4, height: 0, zIndex: 1, marginTop: 8 }} role="progressbar" aria-label="Loading more">
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', [top ? 'top' : 'bottom']: 0 }}>
        <span className="spinner dark" style={{ width: 28, height: 28 }} />
      </div>
    </div>
  );
}
